import {
  IconButton,
  Tooltip,
  Badge,
  Drawer,
  Box,
  Typography,
  List,
  Button,
  Divider,
  Stack,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import { alpha, styled } from '@mui/system';
import { useEffect, useRef, useState } from 'react';
import ShoppingCartTwoToneIcon from '@mui/icons-material/ShoppingCartTwoTone';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCartItems,
  selectCartTotal,
  selectPetLicenseCartItems,
  removeFromCart,
  removePetLicenseFromCart,
  clearPetLicenseFromCart
} from 'src/redux-store/slices/cartSlice';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from 'src/redux-store/appStore';
import { httpRequest } from 'src/services/httpRequest';
import { getFeesForCurrentPermit } from 'src/pages/Permits/common/getFeesForCurrentPermit';
import { handleError } from 'src/redux-store/slices/errorSlice';
import toast from 'react-hot-toast';
import {
  computePetCartLineFeeBreakdown,
  computePetCartLineTotalBeforeTax,
  computePetCartMoneyTotals,
  type PetLicenseCartItem,
  type PetLicenseLineFeeBreakdown
} from 'src/utils/petLicenseCart';
import PetsIcon from '@mui/icons-material/Pets';
import { canUsePetLicenseBundleCheckout } from 'src/utils/petLicenseBundleEligibility';

/** Teal accent aligned with pet license checkout mockups */
const PET_LICENSE_TEAL = '#00acc1';
const PET_LICENSE_TEAL_HOVER = '#00838f';

function FeeBifurcationSnippet ({
  breakdown
}: {
  breakdown: PetLicenseLineFeeBreakdown;
}) {
  const row = (label: string, amount: number) => (
    <Stack
      key={label}
      direction="row"
      justifyContent="space-between"
      sx={{ mt: 0.35 }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="caption">${amount.toFixed(2)}</Typography>
    </Stack>
  );
  return (
    <Box sx={{ mt: 1.25, pl: 1, borderLeft: `2px solid ${PET_LICENSE_TEAL}` }}>
      {row('License Fee', breakdown.licenseFee)}
      {row('State Registration', breakdown.stateRegistration)}
      {row('Pilot Clinic', breakdown.pilotClinic)}
      {breakdown.animalPopulationControl > 0
        ? row('Animal Population Control', breakdown.animalPopulationControl)
        : null}
      {row('Subtotal', breakdown.subtotal)}
      {row(
        breakdown.lateFeesWaived ? 'Late Fee (waived)' : 'Late Fee',
        breakdown.lateFee
      )}
      {row('Total (before processing fee)', breakdown.totalBeforeTax)}
    </Box>
  );
}

const CartBadge = styled(Badge)(
  ({ theme }) => `
    .MuiBadge-badge {
        background-color: ${alpha(theme.palette.error.main, 0.1)};
        color: ${theme.palette.error.main};
        min-width: 16px; 
        height: 16px;
        padding: 0;

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
            content: "";
        }
    }
`
);

const StyledDrawer = styled(Drawer)(
  ({ theme }) => `
    .MuiDrawer-paper {
      min-width: 374px;
      padding: ${theme.spacing(2)};
    }
  `
);

export const Cart: React.FC = () => {
  const ref = useRef<any>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const cartItems = useSelector(selectCartItems);
  const petLicenseItems = useSelector(selectPetLicenseCartItems);
  const total = useSelector(selectCartTotal);
  const userDetails = useSelector((s: RootState) => s.user.userDetails);
  const currentPermitDetails = useSelector(
    (s: RootState) => s.currentPermit.currentPermitDetails
  );

  const canPetBundle = canUsePetLicenseBundleCheckout(
    currentPermitDetails?.currentPermitUserRole,
    userDetails?.UserRole
  );

  const [feeObject, setFeeObject] = useState<Record<string, number>>({});
  const [loadingPetFees, setLoadingPetFees] = useState(false);
  const [payingPet, setPayingPet] = useState(false);

  const itemCount =
    (cartItems?.length ?? 0) +
    (canPetBundle ? petLicenseItems.length : 0);

  const townshipId =
    params.townshipId ||
    currentPermitDetails?.townshipId ||
    String(userDetails?.TownshipID ?? '');
  const department =
    params.department || currentPermitDetails?.departmentName || '';
  const permit = params.permit || String(currentPermitDetails?.id || 10);

  useEffect(() => {
    if (!canPetBundle && petLicenseItems.length > 0) {
      dispatch(clearPetLicenseFromCart());
    }
  }, [canPetBundle, petLicenseItems.length, dispatch]);

  useEffect(() => {
    if (!isDrawerOpen || !petLicenseItems.length || !canPetBundle) return;
    let cancelled = false;
    (async () => {
      setLoadingPetFees(true);
      try {
        const feeResponse = await httpRequest(
          'GET',
          `/configuration/fee_details?PermitID=10`
        );
        if (!cancelled && feeResponse?.status === 'success') {
          setFeeObject(getFeesForCurrentPermit(feeResponse.data) || {});
        }
      } finally {
        if (!cancelled) setLoadingPetFees(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDrawerOpen, petLicenseItems.length, canPetBundle]);

  const petMoney =
    canPetBundle && petLicenseItems.length > 0
      ? computePetCartMoneyTotals(petLicenseItems, feeObject)
      : null;

  const petLineTotalDisplay = (row: PetLicenseCartItem): number => {
    const preTax = computePetCartLineTotalBeforeTax(row, feeObject);
    const pct = petMoney?.taxPercent ?? 0;
    return preTax * (1 + pct / 100);
  };

  const handleOpen = (): void => {
    setIsDrawerOpen(true);
  };

  const handleClose = (): void => {
    setIsDrawerOpen(false);
  };

  const handleRemoveSubscriptionItem = (permitId: number): void => {
    dispatch(removeFromCart({ permitId }));
  };

  const handleRemovePetItem = (applicationId: number): void => {
    dispatch(removePetLicenseFromCart({ applicationId }));
    toast.success('Removed from cart');
  };

  const handleSubscriptionCheckout = (): void => {
    handleClose();
    navigate(`/payment-methods`, {
      state: {
        isSubscriptionPayment: true,
        subscriptionDetails: cartItems
      }
    });
  };

  const handlePetLicensePay = async (): Promise<void> => {
    if (!canPetBundle) {
      toast.error('Bundled pet license payment is only available to applicants.');
      return;
    }
    if (!petLicenseItems.length) {
      toast.error('Your cart has no pet license applications.');
      return;
    }
    const primary = petLicenseItems[0];
    if (!primary?.ApplicationID) {
      toast.error('Invalid cart.');
      return;
    }
    if (
      userDetails?.UserID &&
      primary.UserID &&
      primary.UserID !== userDetails.UserID
    ) {
      toast.error('Cart items must belong to your account.');
      return;
    }
    if (!townshipId || !department) {
      toast.error('Unable to build return URL. Open this page from your permit portal.');
      return;
    }
    if (!petMoney) return;

    setPayingPet(true);
    try {
      const departmentName =
        currentPermitDetails?.departmentName || 'Clerk Department';
      const returnUrl =
        window.location.origin +
        `/${townshipId}/${encodeURIComponent(department)}/${permit}/view-application/${primary.ApplicationID}`;

      const subtotal = petMoney.combinedBeforeTax;
      const taxAmount = petMoney.totalDue - subtotal;

      const createCartPayload = {
        PermitID: 10,
        ApplicationID: primary.ApplicationID,
        CartApplicationIDs: petLicenseItems.map((x) => x.ApplicationID),
        ApplicationNumber: primary.ApplicationNumber || null,
        ApplicationStatus: 'Approved',
        PermitPrefix: 'Pet',
        UserID: primary.UserID || userDetails?.UserID || null,
        UserName: `${userDetails?.FirstName || ''} ${userDetails?.LastName || ''}`.trim(),
        UserEmail: (userDetails as { EmailAddress?: string })?.EmailAddress || null,
        UserPhone: userDetails?.PhoneNumber || null,
        ApplicationFee: petMoney.combinedBeforeTax,
        InspectionFee: 0,
        TaxRateAmount: petMoney.taxPercent,
        TotalAmount: petMoney.totalDue,
        Subtotal: subtotal,
        TaxAmount: taxAmount,
        ApplicantName: primary.OwnerName || null,
        OwnerName: primary.OwnerName || null,
        OwnerAddress: null,
        OwnerEmailAddress: null,
        OwnerContactNumber: null,
        ApplicationType: null,
        PetName: primary.PetName || null,
        PetBreed: null,
        PetType: primary.PetType || null,
        TownshipID: Number(townshipId) || null,
        DepartmentName: departmentName,
        ReturnUrl: returnUrl
      };

      const cartResponse = await httpRequest(
        'POST',
        '/payments/createCart',
        createCartPayload
      );

      if (
        cartResponse?.status === 'success' &&
        cartResponse?.data?.data?.checkout_link
      ) {
        dispatch(clearPetLicenseFromCart());
        window.location.href = cartResponse.data.data.checkout_link;
      } else {
        setPayingPet(false);
        const msg =
          cartResponse?.message || 'Failed to create cart. Please try again.';
        toast.error(msg);
        dispatch(handleError(msg));
      }
    } catch (e) {
      console.error(e);
      setPayingPet(false);
      toast.error('Failed to start payment.');
      dispatch(handleError('Failed to start payment.'));
    }
  };

  const hasSubscriptions = (cartItems?.length ?? 0) > 0;
  const hasPetLicenses = canPetBundle && (petLicenseItems?.length ?? 0) > 0;
  const isEmpty = !hasSubscriptions && !hasPetLicenses;

  return (
    <>
      <Tooltip arrow title="Cart">
        <IconButton color="primary" ref={ref} onClick={handleOpen}>
          <CartBadge
            badgeContent={itemCount > 0 ? itemCount : null}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <ShoppingCartTwoToneIcon />
          </CartBadge>
        </IconButton>
      </Tooltip>

      <StyledDrawer anchor="right" open={isDrawerOpen} onClose={handleClose}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', mb: 2 }}>
            <IconButton sx={{ p: 0, mr: 2 }} onClick={handleClose}>
              <ChevronRightIcon />
            </IconButton>
            <Typography variant="h6" component="h2" sx={{ fontWeight: '700' }}>
              Cart ({itemCount} items)
            </Typography>
          </Box>
          <Divider />

          {isEmpty ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}
            >
              <ShoppingCartTwoToneIcon
                sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                Your cart is empty
              </Typography>
            </Box>
          ) : (
            <>
              <List sx={{ flexGrow: 1, overflow: 'auto' }}>
                {hasSubscriptions
                  ? cartItems.map((item) => (
                    <Card key={item.PermitID} sx={{ mb: 2 }}>
                      <CardContent>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: '700' }}
                            >
                              {item.PermitName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.Period.charAt(0).toUpperCase() +
                                item.Period.slice(1)}{' '}
                              Plan
                            </Typography>
                            <Typography variant="h6" color="primary">
                              ${item.Price}
                            </Typography>
                          </Box>
                          <IconButton
                            onClick={() =>
                              handleRemoveSubscriptionItem(item.PermitID)
                            }
                            color="error"
                            size="small"
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))
                  : null}

                {hasPetLicenses ? (
                  <>
                    {hasSubscriptions ? (
                      <Divider sx={{ my: 2 }} />
                    ) : null}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ color: PET_LICENSE_TEAL, mb: 1.5, mt: hasSubscriptions ? 0 : 1 }}
                    >
                      <PetsIcon sx={{ fontSize: 22 }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
                        Pet Licenses ({petLicenseItems.length})
                      </Typography>
                    </Stack>
                    {loadingPetFees ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={28} sx={{ color: PET_LICENSE_TEAL }} />
                      </Box>
                    ) : null}
                    {petLicenseItems.map((row) => (
                      <Card
                        key={row.ApplicationID}
                        variant="outlined"
                        sx={{
                          mb: 1.5,
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                        }}
                      >
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box sx={{ pr: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  lineHeight: 1.3
                                }}
                              >
                                {row.PetName || '—'} ({row.PetType || '—'})
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                              >
                                {row.ApplicationNumber || `Application #${row.ApplicationID}`}
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '1.1rem',
                                  color: PET_LICENSE_TEAL,
                                  mt: 1
                                }}
                              >
                                ${petLineTotalDisplay(row).toFixed(2)}
                              </Typography>
                              {!loadingPetFees &&
                              Object.keys(feeObject).length > 0 ? (
                                <FeeBifurcationSnippet
                                  breakdown={computePetCartLineFeeBreakdown(
                                    row,
                                    feeObject
                                  )}
                                />
                              ) : null}
                            </Box>
                            <IconButton
                              onClick={() => handleRemovePetItem(row.ApplicationID)}
                              color="error"
                              size="small"
                              aria-label="Remove from cart"
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </>
                ) : null}
              </List>

              <Box sx={{ mt: 2 }}>
                <Divider sx={{ my: 2 }} />
                {hasSubscriptions ? (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="h6">Subscription total:</Typography>
                    <Typography variant="h6" color="primary">
                      ${total}
                    </Typography>
                  </Stack>
                ) : null}
                {hasPetLicenses && petMoney ? (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2, mt: 1 }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                      Total:
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.15rem',
                        color: PET_LICENSE_TEAL
                      }}
                    >
                      ${petMoney.totalDue.toFixed(2)}
                    </Typography>
                  </Stack>
                ) : null}

                {hasSubscriptions ? (
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mb: hasPetLicenses ? 1 : 0 }}
                    onClick={handleSubscriptionCheckout}
                  >
                    Proceed to subscription checkout
                  </Button>
                ) : null}
                {hasPetLicenses ? (
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={payingPet || loadingPetFees}
                    startIcon={<PetsIcon />}
                    onClick={() => void handlePetLicensePay()}
                    sx={{
                      py: 1.25,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: '1rem',
                      bgcolor: PET_LICENSE_TEAL,
                      color: '#fff',
                      boxShadow: 2,
                      '&:hover': {
                        bgcolor: PET_LICENSE_TEAL_HOVER
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'action.disabledBackground',
                        color: 'action.disabled'
                      }
                    }}
                  >
                    {payingPet ? 'Redirecting…' : 'Pay for Pet Licenses'}
                  </Button>
                ) : null}
              </Box>
            </>
          )}
        </Box>
      </StyledDrawer>
    </>
  );
};

export default Cart;
